import { Alert, Card, Empty, Progress, Skeleton, Tag, Tooltip } from "antd"
import { PageHeader } from "../../shared/components/page-header.js"
import { useClientHealth, type TenantHealth } from "../../features/health/use-client-health.js"
import { healthBand } from "../../features/health/health-score.js"

/**
 * ⚠ **The card shows its own inputs, and that is the design, not a debugging aid.** Nobody has defined what
 * "healthy" means for a RegDesk customer; the request arrived as a section title. A single number under that
 * title would be believed, acted on, and impossible to argue with — and the first time CS disagreed with it
 * there would be no way to say *why* it was wrong. So every card carries the three signals and the raw numbers
 * each was computed from. The score is the headline; the evidence is underneath it.
 */

const TONE_COLOR = {
  good: "var(--csbi-status-good-ink)",
  warning: "var(--csbi-status-warning-ink)",
  serious: "var(--csbi-status-serious-ink)",
  unknown: "var(--csbi-ink-muted)",
} as const

function HealthCard({ row }: { row: TenantHealth }) {
  const band = healthBand(row.score)

  return (
    <Card size="small" styles={{ body: { padding: 16 } }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 12 }}>
        <span style={{ fontWeight: 650, fontSize: 14, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>
          {row.tenant.displayName}
        </span>
        {/* The band is named, never colour alone — the same rule the status palette follows everywhere else */}
        <Tag color={band.tone === "unknown" ? "default" : undefined} style={{ marginInlineEnd: 0 }}>
          {band.label}
        </Tag>
        <span style={{ marginLeft: "auto", fontSize: 22, fontWeight: 650, color: TONE_COLOR[band.tone] }}>
          {row.score ?? "—"}
        </span>
      </div>

      {row.available < row.total && (
        <p style={{ margin: "0 0 10px", fontSize: 12, color: "var(--csbi-ink-secondary)" }}>
          Averaged over {row.available} of {row.total} signals — the rest had no data in this period.
        </p>
      )}

      <div style={{ display: "grid", gap: 10 }}>
        {row.components.map((c) => (
          <div key={c.key}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 2 }}>
              <span>{c.label}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", color: "var(--csbi-ink-secondary)" }}>
                {c.score ?? "no data"}
              </span>
            </div>
            <Progress
              percent={c.score ?? 0}
              showInfo={false}
              size="small"
              strokeColor={c.score == null ? "var(--csbi-ink-muted)" : undefined}
            />
            <Tooltip title={c.detail}>
              <div
                style={{
                  fontSize: 11,
                  color: "var(--csbi-ink-muted)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {c.detail}
              </div>
            </Tooltip>
          </div>
        ))}
      </div>
    </Card>
  )
}

export function ClientHealthPage() {
  const { rows, isLoading, errorMessage } = useClientHealth()

  return (
    <>
      <PageHeader title="Client Health" />

      {errorMessage && <Alert type="error" showIcon style={{ marginBottom: 16 }} message={errorMessage} />}

      {isLoading ? (
        <Skeleton active paragraph={{ rows: 6 }} />
      ) : rows.length === 0 ? (
        <Empty description="No customers in the current filter" />
      ) : (
        <div
          style={{
            display: "grid",
            gap: 12,
            gridTemplateColumns: "repeat(auto-fit, minmax(max(280px, (100% - 24px) / 3), 1fr))",
          }}
        >
          {rows.map((row) => (
            <HealthCard key={row.tenant.tenantId} row={row} />
          ))}
        </div>
      )}
    </>
  )
}
